import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button, Typography, Collapse } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('💥 [ErrorBoundary] Erreur non capturée:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F8FAFC',
            padding: 24,
          }}
        >
          <div
            style={{
              maxWidth: 600,
              width: '100%',
              backgroundColor: '#FFFFFF',
              borderRadius: 16,
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.02)',
              border: '1px solid #E2E8F0',
              padding: 32,
            }}
          >
            <Result
              status="500"
              title="Une erreur inattendue est survenue"
              subTitle="L'application a rencontré un dysfonctionnement temporaire. Vous pouvez actualiser la page ou revenir à l'accueil."
              extra={[
                <Button
                  key="reload"
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={this.handleReload}
                  style={{
                    backgroundColor: '#0A543D',
                    borderColor: '#0A543D',
                    borderRadius: 8,
                    height: 40,
                    fontWeight: 500,
                  }}
                >
                  Actualiser la page
                </Button>,
                <Button
                  key="home"
                  icon={<HomeOutlined />}
                  onClick={this.handleGoHome}
                  style={{
                    borderRadius: 8,
                    height: 40,
                  }}
                >
                  Tableau de bord
                </Button>,
              ]}
            >
              {this.state.error && (
                <Collapse
                  ghost
                  style={{ marginTop: 16 }}
                  items={[
                    {
                      key: 'details',
                      label: (
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                          Détails techniques de l'erreur
                        </Typography.Text>
                      ),
                      children: (
                        <div
                          style={{
                            maxHeight: 180,
                            overflowY: 'auto',
                            backgroundColor: '#F1F5F9',
                            padding: 12,
                            borderRadius: 8,
                            fontSize: 12,
                            fontFamily: 'monospace',
                            color: '#DC2626',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {this.state.error.toString()}
                          {this.state.errorInfo?.componentStack}
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </Result>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
